import React, { useMemo } from 'react'
import { sidebarPalette, sidebarActiveItemSx } from './sidebarStyles'
import type { Item } from './Sidebar'
import { NavLink } from 'react-router-dom'
import { useCompanyDetailsContext } from '../../../utils/hooks/useCompanyDetailsContext'
import { useSidebarActiveRouteParams } from '../../../utils/hooks/useSidebarActiveRouteParams'
import ListItemIcon from '@mui/material/ListItemIcon'
import LockIcon from '@mui/icons-material/Lock'
import ListItemText from '@mui/material/ListItemText'
import Collapse from '@mui/material/Collapse'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import PauseIcon from '@mui/icons-material/Pause'
import { ListItemButton, Tooltip } from '@mui/material'
import { AdGroupStatus, Status } from '../../../__generated__/graphql'
import MenuWithIcon from '../MenuWithIcon'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import HasPermission from '../../permissions/HasPermission'
import { useConfirmDialogContext } from '../../../utils/providers/confirmDialogContext'
import { FileCopy } from '@mui/icons-material'
import { useDuplicateAdGroupOrPosition } from '../../../utils/hooks/useDuplicateAdGroupOrPosition'
import BackdropLoader from '../../loader/BackdropLoader'
import { SidebarPublisherActions } from './SidebarPublisherActions'
import { SidebarAdvertiserActions } from './SidebarAdvertiserActions'

const listIconsSx = {
  minWidth: 'inherit',
  paddingRight: '1rem',
  color: 'inherit',
}

const subItemsSx = {
  '& div span': {
    textTransform: 'none',
    fontWeight: 'normal',
  },
  color: sidebarPalette.light,
  '&:hover .status-active': {
    color: 'success.main',
  },
  '&:hover .status-inactive, &:hover .status-paused': {
    color: 'warning.main',
  },
  '& .lock': {
    transform: 'scale(0.8)',
  },
  '&:hover': {
    backgroundColor: sidebarPalette.dark,
  },
  '&.active span, &.active svg': {
    color: sidebarPalette.text,
  },
  '&.active .status-active': {
    color: 'success.main',
  },
  '&.active .status-inactive, &.active .status-paused': {
    color: 'warning.main',
  },
}

export const Items: React.FC<{
  item: Item
  deployAll: boolean
  updateSidebarItems: () => void
}> = ({ item, deployAll = false, updateSidebarItems }) => {
  const { siteId, campaignId, positionId, adgroupId, campaignProgrammaticId, inventoryId } =
    useSidebarActiveRouteParams()
  const {
    company: { type: companyType, slug: companySlug },
  } = useCompanyDetailsContext()

  const itemFoundInPath = item.id === (siteId || campaignId || campaignProgrammaticId)

  const subItemsIds = item.subItems.map((subItem) => subItem.id)
  const activeSubItemId = positionId ?? adgroupId ?? inventoryId
  const subItemIsFromItem = activeSubItemId !== undefined && subItemsIds.includes(activeSubItemId)

  const displaySubItems = deployAll || itemFoundInPath || subItemIsFromItem

  const lowerCompanyType = companyType.toLowerCase()
  const subItemLink = companyType === 'Publisher' ? 'site' : 'campaign'

  const sortedSubItems = sortItemsByStatus(item.subItems).map((subItem) => (
    <SubItems key={subItem.id} subItem={subItem} />
  ))

  return (
    <>
      <ListItemButton
        disableRipple
        component={NavLink}
        activeclassname="active"
        to={`/${lowerCompanyType}/${companySlug}/${subItemLink}/${item.id}`}
        autoFocus={displaySubItems}
        sx={sidebarActiveItemSx}
      >
        {item.status === Status.Archived && (
          <ListItemIcon sx={listIconsSx}>
            <LockIcon className={'lock'} fontSize="small" />
          </ListItemIcon>
        )}
        <ListItemText>{item.name}</ListItemText>
        {displaySubItems && (
          <HasPermission roleRequired="edit">
            {companyType === 'Publisher' ? (
              <SidebarPublisherActions item={item} updateSidebarItems={updateSidebarItems} />
            ) : (
              <SidebarAdvertiserActions item={item} updateSidebarItems={updateSidebarItems} />
            )}
          </HasPermission>
        )}
      </ListItemButton>
      <Collapse in={displaySubItems} unmountOnExit>
        {sortedSubItems}
      </Collapse>
    </>
  )
}

const SubItems: React.FC<{
  subItem: Item['subItems'][number]
}> = ({ subItem }) => {
  const t = useAppTranslations()
  const { adgroupId, positionId } = useSidebarActiveRouteParams()
  const isSelected = subItem.id === (adgroupId || positionId)
  const {
    company: { type: companyType, slug: companySlug },
  } = useCompanyDetailsContext()

  const lowerCompanyType = companyType.toLowerCase()
  const subItemLink = companyType === 'Publisher' ? 'position' : 'adgroup'
  const isAdGroup = subItemLink === 'adgroup'

  const [duplicateSubItem, duplicateLoading] = useDuplicateAdGroupOrPosition(subItem.id, subItem.name)
  const setConfirmDialogState = useConfirmDialogContext()

  const actionsItems = useMemo(
    () => [
      {
        text: t('commons.actions.duplicate'),
        icon: <FileCopy fontSize="small" />,
        action: () => {
          setConfirmDialogState({
            message: [
              t('commons.actions.confirmDuplicate', { name: subItem.name }),
              ...(isAdGroup ? [t('adgroup.duplicateStatusWarning')] : []),
            ],
            buttonLabel: t('commons.actions.duplicate'),
            onSubmit: duplicateSubItem,
          })
        },
        autoClose: true,
      },
    ],
    [subItem, setConfirmDialogState, duplicateSubItem]
  )

  return (
    <ListItemButton
      key={subItem.name}
      autoFocus={true}
      disableRipple
      component={NavLink}
      activeclassname="active"
      to={`/${lowerCompanyType}/${companySlug}/${subItemLink}/${subItem.id}`}
      sx={{
        ...sidebarActiveItemSx,
        ...subItemsSx,
        '&.Mui-focusVisible': {
          backgroundColor: 'inherit',
        },
      }}
    >
      <BackdropLoader open={duplicateLoading} />
      {subItem.status && <SubItemIcon status={subItem.status} />}
      <ListItemText data-testid="menuSubItem">{subItem.name}</ListItemText>
      {isSelected && (
        <HasPermission roleRequired="edit">
          <MenuWithIcon
            actionItems={actionsItems}
            dataId="sidebarMenuSubItemIcon"
            size="small"
            iconButtonSx={{ py: 0, color: 'white' }}
          >
            <MoreVertIcon fontSize="small" />
          </MenuWithIcon>
        </HasPermission>
      )}
    </ListItemButton>
  )
}

const SubItemIcon: React.FC<{ status: Status | AdGroupStatus }> = ({ status }) => {
  const t = useAppTranslations()

  const statusIcon = useMemo(() => {
    switch (status) {
      case Status.Active:
        return (
          <Tooltip title={t('commons.active')}>
            <PlayArrowIcon className={'status-active'} fontSize="small" />
          </Tooltip>
        )
      case Status.Archived:
        return (
          <Tooltip title={t('commons.archived')}>
            <LockIcon className={'lock'} fontSize="small" />
          </Tooltip>
        )
      case Status.Inactive:
        return (
          <Tooltip title={t('commons.inactive')}>
            <StopIcon className={'status-inactive'} fontSize="small" />
          </Tooltip>
        )
      default:
        return (
          <Tooltip title={t('commons.paused')}>
            <PauseIcon className={'status-paused'} fontSize="small" />
          </Tooltip>
        )
    }
  }, [status, t])

  return <ListItemIcon sx={listIconsSx}>{statusIcon}</ListItemIcon>
}

const sortItemsByStatus = (items: Item['subItems']): Item['subItems'] => {
  const sortedSubItemsMap = items.slice().reduce((previous, current) => {
    const isArchived = current.status === Status.Archived
    const isActive = current.status === Status.Active

    const status = isArchived ? Status.Archived : isActive ? Status.Active : Status.Inactive

    const items: Item['subItems'] = previous.get(status) ?? []
    items.push(current)

    return previous.set(status, items)
  }, new Map<Status, Item['subItems']>())

  return (sortedSubItemsMap.get(Status.Active) ?? [])
    .sort((a, b) => a.name.localeCompare(b.name))
    .concat(
      (sortedSubItemsMap.get(Status.Inactive) ?? []).sort((a, b) => a.name.localeCompare(b.name)),
      (sortedSubItemsMap.get(Status.Archived) ?? []).sort((a, b) => a.name.localeCompare(b.name))
    )
}
